import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { slotLabel } from '../lib/rules'
import type { UnitPosition } from '../lib/types'

const SEASON = 2025

interface WeekRow { week_no: number; points: number; opponent: string | null; is_home: boolean | null }
interface PlayerRow { player_id: string; player_name: string; weeks: number; points: number; share: number }
interface Summary {
  avg_points: number; total_points: number; best_week: number
  last4_avg: number | null; pos_rank: number; pos_total: number
  trend_delta: number | null; weeks_played: number
}

/**
 * The right rail: a permanent inspector rather than a modal.
 *
 * The stats used to live in a centred sheet that covered the board. That is
 * the wrong shape for this decision — comparing two rooms meant opening,
 * reading, closing, opening, and holding the first set of numbers in your
 * head while you did it. Docked, the numbers sit beside the cards and change
 * as you move between them, so comparison is just looking left and right.
 *
 * It also solves onboarding for free, which is the part worth noticing. A
 * first-time manager who has not clicked anything is looking at an empty
 * rail, and an empty rail is the most valuable teaching space in the product:
 * it is exactly where their attention already is, and it costs a returning
 * player nothing because one click replaces it with what they came for. No
 * tour, no dismissible overlay, nothing to build twice.
 */
export default function RoomRail({
  team, position, opponent, pickable, onPick, onClose, slotsFilled, slotsTotal,
}: {
  team: string | null
  position: UnitPosition | null
  opponent: string | null
  pickable?: boolean
  onPick?: () => void
  onClose?: () => void
  slotsFilled: number
  slotsTotal: number
}) {
  const [weeks, setWeeks] = useState<WeekRow[]>([])
  const [players, setPlayers] = useState<PlayerRow[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!team || !position) { setSummary(null); setWeeks([]); setPlayers([]); return }
    let alive = true
    void (async () => {
      setLoading(true)
      const [d, p, s] = await Promise.all([
        supabase.rpc('unit_season_detail', { p_season: SEASON, p_team: team, p_position: position }),
        supabase.rpc('unit_season_players', { p_season: SEASON, p_team: team, p_position: position }),
        supabase.from('v_unit_season').select('*')
          .eq('season', SEASON).eq('team', team).eq('position', position).maybeSingle(),
      ])
      if (!alive) return
      setWeeks((d.data ?? []) as WeekRow[])
      setPlayers((p.data ?? []) as PlayerRow[])
      setSummary((s.data ?? null) as Summary | null)
      setLoading(false)
    })()
    return () => { alive = false }
  }, [team, position])

  if (!team || !position) return <RailGuide filled={slotsFilled} total={slotsTotal} />

  const vsOpp = opponent ? weeks.filter((w) => w.opponent === opponent) : []
  const max = Math.max(1, ...weeks.map((w) => w.points))
  const trend = summary?.trend_delta ?? null

  return (
    <aside className="rail" aria-label={`${team} ${slotLabel(position)} stats`}>
      <header className="rail-head">
        <div>
          <span className="rail-eyebrow">{slotLabel(position)} room</span>
          <h2>{team}</h2>
          {opponent && <p className="muted small">this week vs {opponent}</p>}
        </div>
        {onClose && (
          <button className="rail-x" onClick={onClose} aria-label="Close stats">×</button>
        )}
      </header>

      {loading && <p className="muted pad">Loading {SEASON}…</p>}

      {!loading && !summary && (
        <p className="muted pad">No {SEASON} history for this room yet.</p>
      )}

      {!loading && summary && (
        <div className="rail-body">
          <div className="rail-stats">
            <div><span className="ss-n">{summary.avg_points}</span><span className="ss-l">pts / week</span></div>
            <div>
              <span className="ss-n">#{summary.pos_rank}</span>
              <span className="ss-l">of {summary.pos_total}</span>
            </div>
            <div><span className="ss-n">{summary.best_week}</span><span className="ss-l">best week</span></div>
            <div>
              <span className={`ss-n ${trend && trend > 0 ? 'is-up' : trend && trend < 0 ? 'is-down' : ''}`}>
                {trend === null ? '—' : `${trend > 0 ? '+' : ''}${trend}`}
              </span>
              <span className="ss-l">last 4 vs avg</span>
            </div>
          </div>

          {opponent && (
            <section className="rail-block">
              <h3>Against {opponent}</h3>
              {vsOpp.length === 0 ? (
                <p className="muted small">They didn't meet in {SEASON}.</p>
              ) : (
                <ul className="h2h">
                  {vsOpp.map((w) => (
                    <li key={w.week_no}>
                      <span className="h2h-wk">Wk {w.week_no}</span>
                      <strong className={w.points >= summary.avg_points ? 'is-up' : 'is-down'}>
                        {w.points}
                      </strong>
                      <small className="muted">
                        {w.points >= summary.avg_points ? 'above' : 'below'} average
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="rail-block">
            <h3>{SEASON} week by week</h3>
            <div className="spark">
              {weeks.map((w) => (
                <span
                  key={w.week_no}
                  className={opponent && w.opponent === opponent ? 'sp is-vs' : 'sp'}
                  style={{ height: `${Math.max(4, (w.points / max) * 100)}%` }}
                  title={`Week ${w.week_no} — ${w.points}`}
                />
              ))}
            </div>
            <p className="muted small">{weeks.length} weeks · tallest {max}</p>
          </section>

          <section className="rail-block">
            <h3>Who's in the room</h3>
            <ul className="roomers">
              {players.slice(0, 6).map((p) => (
                <li key={p.player_id}>
                  <span className="rm-name">{p.player_name}</span>
                  <span className="rm-bar"><i style={{ width: `${p.share}%` }} /></span>
                  <span className="rm-share">{p.share}%</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}

      {onPick && (
        <footer className="rail-foot">
          <button className="btn btn-primary full" disabled={!pickable} onClick={onPick}>
            {pickable ? `Spend ${team} ${slotLabel(position)}` : 'Not available'}
          </button>
          <p className="window-fine">
            {SEASON} numbers. Rosters move — form, not a projection.
          </p>
        </footer>
      )}
    </aside>
  )
}

/**
 * What the rail says before anything is selected.
 *
 * Written as four things that happen in order, because the one genuinely
 * unusual idea in Gridlock — you are drafting a room, and spending it costs
 * you that room for the season — is the one a new manager has no prior
 * fantasy experience to fall back on. The progress line at the top means a
 * returning player can see where they are without reading any of it.
 */
function RailGuide({ filled, total }: { filled: number; total: number }) {
  const done = filled >= total
  return (
    <aside className="rail rail-guide" aria-label="How to play">
      <header className="rail-head">
        <div>
          <span className="rail-eyebrow">How it works</span>
          <h2>{done ? 'Lineup set' : 'Your first pick'}</h2>
        </div>
      </header>

      <div className="rail-progress">
        <span className="rp-bar"><i style={{ width: `${(filled / Math.max(1, total)) * 100}%` }} /></span>
        <span className="rp-n">{filled} of {total} slots filled</span>
      </div>

      <ol className="rail-steps">
        <li>
          <b>Take a room, not a player.</b>
          Pick <em>CIN QB</em> and every quarterback Cincinnati plays that week
          scores for you — starter, backup, the third-stringer in garbage time.
          No injury news to chase.
        </li>
        <li>
          <b>Spend it and it's gone.</b>
          Each of the 192 rooms is yours <em>once all season</em>. Burn Buffalo's
          quarterbacks in Week 1 and you have 17 weeks without them.
        </li>
        <li>
          <b>That's the whole game.</b>
          The good matchups are obvious. The skill is deciding <em>when</em> to
          cash one, and what you keep in reserve for December.
        </li>
        <li>
          <b>Check the numbers first.</b>
          Tap <em>stats</em> on any card and this panel fills with last season's
          scoring, the rank, the trend, and how that room did against the team
          it's about to face.
        </li>
      </ol>

      <p className="rail-fine">
        Picks lock at the first kickoff of the week. Nothing is final until then —
        change your mind as often as you like.
      </p>
    </aside>
  )
}
